import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import api from '@/services/api.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { OT_KIND_OPTIONS, SALARY_HEAD_TYPE_OPTIONS } from './shared/hrmsPolicyOptions';

const { Text } = Typography;

type CoaAccount = {
  id: string;
  account_code?: string;
  account_name?: string;
  is_active?: boolean;
  is_group?: boolean;
};

const SalaryHeadsPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const isOtHead = Form.useWatch('is_ot_head', form);
  const otKind = Form.useWatch('ot_kind', form);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.salaryHeads(),
    queryFn: () => hrmsService.listSalaryHeads(),
  });

  const { data: coa = [] } = useQuery({
    queryKey: ['chart-of-accounts', 'salary-heads'],
    queryFn: async () => {
      const res = await api.get<CoaAccount[]>('/finance/chart-of-accounts');
      const list = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? [];
      return (list as CoaAccount[]).filter((a) => a.is_active !== false && a.is_group !== true);
    },
    staleTime: 60_000,
  });

  const coaOptions = useMemo(
    () =>
      coa.map((a) => ({
        value: a.id,
        label: `${a.account_code ?? ''} — ${a.account_name ?? a.id}`.trim(),
      })),
    [coa]
  );

  const coaLabel = (id?: string | null) => {
    if (!id) return '—';
    return coaOptions.find((o) => o.value === id)?.label || '—';
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      component_type: 'Earning',
      is_fixed: true,
      include_in_ot_base: false,
      include_in_eosb_base: false,
      is_ot_head: false,
      is_active: true,
      sort_order: 100,
    });
    setDrawerOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({
      ...r,
      ot_rate_multiplier: r.ot_rate_multiplier != null ? Number(r.ot_rate_multiplier) : undefined,
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = {
        ...v,
        gl_expense_account_id: v.gl_expense_account_id || null,
        ot_kind: v.is_ot_head ? v.ot_kind : null,
        ot_rate_multiplier: v.is_ot_head ? v.ot_rate_multiplier : null,
      };
      if (editing) await hrmsService.updateSalaryHead(editing.id, body);
      else await hrmsService.createSalaryHead(body);
      message.success(editing ? 'Updated' : 'Created');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryHeads() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await hrmsService.deleteSalaryHead(id);
      message.success('Deleted');
      queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryHeads() });
    } catch {
      message.error('Delete failed');
    }
  };

  const columns: ColumnsType<any> = [
    { title: 'Code', dataIndex: 'component_code', width: 110 },
    { title: 'Name', dataIndex: 'component_name' },
    {
      title: 'Type',
      dataIndex: 'component_type',
      width: 100,
      render: (t: string) => <Tag color={t === 'Earning' ? 'green' : 'orange'}>{t}</Tag>,
    },
    {
      title: 'Expense account',
      dataIndex: 'gl_expense_account_id',
      ellipsis: true,
      render: (id: string) => <Text type="secondary">{coaLabel(id)}</Text>,
    },
    {
      title: 'Cost center',
      dataIndex: 'cost_center_code',
      width: 110,
      render: (v: string) => v || '—',
    },
    {
      title: 'OT',
      key: 'ot',
      width: 160,
      render: (_, r) =>
        r.is_ot_head ? (
          <Space direction="vertical" size={0}>
            <Tag color="blue">{r.ot_kind || 'OT'}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ×{Number(r.ot_rate_multiplier || 0).toFixed(3)}
            </Text>
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'OT base',
      dataIndex: 'include_in_ot_base',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="geekblue">Yes</Tag> : '—'),
    },
    {
      title: 'EOSB',
      dataIndex: 'include_in_eosb_base',
      width: 70,
      render: (v: boolean) => (v ? <Tag color="purple">Yes</Tag> : '—'),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 100,
      render: (_, r) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete head?" onConfirm={() => remove(r.id)}>
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Salary heads"
          subtitle="Earnings/deductions, per-head GL expense accounts, and OT kinds with rates"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add head
            </Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Expense account is per salary head"
        description={
          <span>
            Map each earning to a Chart of Accounts expense leaf. Payroll GL handoff posts one debit per mapped head.
            Company default expense / payable on <Link to="/hr/setup">HRMS Setup</Link> is only the fallback for
            unmapped heads and net pay. Create separate OT heads for Normal, Friday, and Holiday with their rates (or
            use <strong>Apply UAE defaults</strong>).
          </span>
        }
      />

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No salary heads — apply UAE defaults from Setup or add BASIC / OT heads"
            />
          ),
        }}
      />

      <Drawer
        title={editing ? 'Edit salary head' : 'New salary head'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        destroyOnClose
        extra={
          <Button type="primary" onClick={save}>
            Save
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed) => {
            if (changed.is_ot_head === true) {
              form.setFieldsValue({
                is_fixed: false,
                component_type: 'Earning',
                ot_kind: form.getFieldValue('ot_kind') || 'Normal',
                ot_rate_multiplier:
                  form.getFieldValue('ot_rate_multiplier') ??
                  OT_KIND_OPTIONS.find((o) => o.value === 'Normal')?.defaultRate,
              });
            }
            if (changed.is_ot_head === false) {
              form.setFieldsValue({ ot_kind: undefined, ot_rate_multiplier: undefined });
            }
            if (changed.ot_kind) {
              const opt = OT_KIND_OPTIONS.find((o) => o.value === changed.ot_kind);
              if (opt && form.getFieldValue('ot_rate_multiplier') == null) {
                form.setFieldsValue({ ot_rate_multiplier: opt.defaultRate });
              }
            }
          }}
        >
          <Form.Item
            name="component_code"
            label="Code"
            rules={[{ required: true }]}
            help="Stable code used in payroll lines, e.g. BASIC, OT_NORMAL"
          >
            <Input disabled={Boolean(editing)} placeholder="BASIC" />
          </Form.Item>
          <Form.Item name="component_name" label="Display name" rules={[{ required: true }]}>
            <Input placeholder="Basic Salary" />
          </Form.Item>
          <Form.Item name="component_type" label="Type" rules={[{ required: true }]}>
            <Select options={[...SALARY_HEAD_TYPE_OPTIONS]} disabled={Boolean(isOtHead)} />
          </Form.Item>

          <Form.Item
            name="gl_expense_account_id"
            label="Salary expense account"
            help="Debit this COA leaf when payroll posts this head. Required for accurate GL handoff."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select expense account"
              options={coaOptions}
            />
          </Form.Item>

          <Form.Item
            name="cost_center_code"
            label="Cost center code"
            help="Optional default cost center for GL / analytics when this head posts"
          >
            <Input placeholder="e.g. CC-OPS" />
          </Form.Item>

          <Divider>Overtime classification</Divider>
          <Form.Item
            name="is_ot_head"
            label="This is an overtime pay head"
            valuePropName="checked"
            help="Turn on for OT_NORMAL / OT_FRIDAY / OT_HOLIDAY style earnings — not for Basic"
          >
            <Switch />
          </Form.Item>
          {isOtHead && (
            <>
              <Form.Item
                name="ot_kind"
                label="OT kind"
                rules={[{ required: true, message: 'Select Normal, Friday, or Holiday' }]}
                help={OT_KIND_OPTIONS.find((o) => o.value === otKind)?.help}
              >
                <Select
                  options={OT_KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  onChange={(v) => {
                    const opt = OT_KIND_OPTIONS.find((o) => o.value === v);
                    if (opt) form.setFieldsValue({ ot_rate_multiplier: opt.defaultRate });
                  }}
                />
              </Form.Item>
              <Form.Item
                name="ot_rate_multiplier"
                label="OT rate (multiplier)"
                rules={[{ required: true, message: 'Enter OT rate' }]}
                help="1.250 = 125% of hourly OT base. Hourly base uses heads marked Include in OT base (usually Basic)."
              >
                <InputNumber min={0.01} max={5} step={0.05} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          <Divider>Calculation flags</Divider>
          <Form.Item
            name="is_fixed"
            label="Amount behaviour"
            valuePropName="checked"
            help="Fixed = contractual; Variable = computed each period (OT heads are variable)"
          >
            <Switch checkedChildren="Fixed" unCheckedChildren="Variable" disabled={Boolean(isOtHead)} />
          </Form.Item>
          <Form.Item
            name="include_in_ot_base"
            label="Include in OT hourly base"
            valuePropName="checked"
            help="Usually Basic only. OT heads themselves should leave this off."
          >
            <Switch disabled={Boolean(isOtHead)} />
          </Form.Item>
          <Form.Item
            name="include_in_eosb_base"
            label="Include in EOSB / gratuity base"
            valuePropName="checked"
            help="UAE: typically Basic only"
          >
            <Switch disabled={Boolean(isOtHead)} />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order" help="Lower numbers appear first on payslips">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </WorkspaceLayout>
  );
};

export default SalaryHeadsPage;
